/*
 * VPDB - Virtual Pinball Database
 * Copyright (C) 2019 freezy <freezy@vpdb.io>
 *
 * This program is free software; you can redistribute it and/or
 * modify it under the terms of the GNU General Public License
 * as published by the Free Software Foundation; either version 2
 * of the License, or (at your option) any later version.
 *
 * This program is distributed in the hope that it will be useful,
 * but WITHOUT ANY WARRANTY; without even the implied warranty of
 * MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE.  See the
 * GNU General Public License for more details.
 *
 * You should have received a copy of the GNU General Public License
 * along with this program; if not, write to the Free Software
 * Foundation, Inc., 51 Franklin Street, Fifth Floor, Boston, MA  02110-1301, USA.
 * Modified for sigmashake-pinball by SigmaShake, 2026.
 */

import type { InputAction, InputEvent } from "../../shared/contracts";

/** The actions that the engine accepts. Any other value is dropped. */
const ACTIONS: ReadonlySet<string> = new Set<InputAction>([
  "left_flipper",
  "right_flipper",
  "plunger",
  "nudge_left",
  "nudge_right",
  "nudge_up",
  "start",
]);

/** The queue holds at most this many events. A flood of events cannot grow it without limit. */
const MAX_QUEUE = 256;

/**
 * The input queue.
 *
 * Upstream this class maps key codes of the keyboard. Here the host app sends
 * an `InputEvent` and the queue keeps it until the next physics step.
 * Events leave the queue in the order they came in.
 */
export class PinInput {
  private readonly queue: InputEvent[] = [];

  /** Adds one event. An unknown action or a full queue drops the event. */
  public push(ev: InputEvent): void {
    if (!ACTIONS.has(ev.action) || this.queue.length >= MAX_QUEUE) {
      return;
    }
    const value =
      typeof ev.value === "number" && Number.isFinite(ev.value)
        ? Math.min(1, Math.max(0, ev.value))
        : undefined;
    this.queue.push(
      value === undefined
        ? { action: ev.action, pressed: ev.pressed === true }
        : { action: ev.action, pressed: ev.pressed === true, value },
    );
  }

  /** Hands every queued event to `handler`, oldest first, and empties the queue. */
  public processKeys(handler: (ev: InputEvent) => void): void {
    while (this.queue.length > 0) {
      const ev = this.queue.shift()!;
      handler(ev);
    }
  }

  public clear(): void {
    this.queue.length = 0;
  }
}
